/**
 * @file Generic auth store for session management
 * @module auth/auth-store
 *
 * @remarks
 * This module provides a generic store for authentication information that can be accessed
 * by handlers that need authentication context. It supports any type of auth info
 * and provides session-based storage with optional TTL support.
 */

import { logger } from "../logger.js";

/**
 * Generic authentication store interface
 */
export interface IAuthStore<T> {
  /**
   * Store auth info for a session
   */
  set(sessionId: string, authInfo: T): void;

  /**
   * Get auth info for a session
   */
  get(sessionId: string): T | undefined;

  /**
   * Remove auth info for a session
   */
  remove(sessionId: string): void;

  /**
   * Clear all auth info
   */
  clear(): void;

  /**
   * Get all session IDs
   */
  keys(): string[];

  /**
   * Get the number of stored sessions
   */
  size(): number;

  /**
   * Check if a session exists
   */
  has(sessionId: string): boolean;
}

/**
 * Session entry with optional metadata
 */
interface SessionEntry<T> {
  authInfo: T;
  createdAt: Date;
  lastAccessed: Date;
  expiresAt?: Date;
}

/**
 * Options for creating an auth store
 */
export interface AuthStoreOptions {
  /**
   * Time-to-live for sessions in milliseconds
   * If not specified, sessions don't expire
   */
  ttl?: number;

  /**
   * Interval for cleaning up expired sessions in milliseconds
   * Default: 5 minutes
   */
  cleanupInterval?: number;

  /**
   * Maximum number of sessions to store
   * If exceeded, oldest sessions are evicted
   */
  maxSessions?: number;
}

/**
 * Generic in-memory auth store implementation
 */
export class AuthStore<T> implements IAuthStore<T> {
  private readonly store = new Map<string, SessionEntry<T>>();
  private readonly options: AuthStoreOptions;
  private cleanupTimer?: NodeJS.Timeout;

  constructor(options: AuthStoreOptions = {}) {
    this.options = {
      cleanupInterval: 5 * 60 * 1000, // 5 minutes default
      ...options,
    };

    // Start cleanup timer if TTL is specified
    if (this.options.ttl && this.options.cleanupInterval) {
      this.startCleanupTimer();
    }
  }

  /**
   * Store auth info for a session
   */
  set(sessionId: string, authInfo: T): void {
    const now = new Date();
    const entry: SessionEntry<T> = {
      authInfo,
      createdAt: now,
      lastAccessed: now,
      expiresAt: this.options.ttl
        ? new Date(now.getTime() + this.options.ttl)
        : undefined,
    };

    // Check max sessions limit
    if (
      this.options.maxSessions &&
      this.store.size >= this.options.maxSessions
    ) {
      this.evictOldestSession();
    }

    this.store.set(sessionId, entry);
    logger.debug(`Stored auth info for session: ${sessionId}`);
  }

  /**
   * Get auth info for a session
   */
  get(sessionId: string): T | undefined {
    const entry = this.store.get(sessionId);

    if (!entry) {
      return undefined;
    }

    // Check if expired
    if (entry.expiresAt && entry.expiresAt < new Date()) {
      this.remove(sessionId);
      logger.debug(`Session expired and removed: ${sessionId}`);
      return undefined;
    }

    // Update last accessed time
    entry.lastAccessed = new Date();

    return entry.authInfo;
  }

  /**
   * Remove auth info for a session
   */
  remove(sessionId: string): void {
    if (this.store.delete(sessionId)) {
      logger.debug(`Removed auth info for session: ${sessionId}`);
    }
  }

  /**
   * Clear all auth info
   */
  clear(): void {
    const count = this.store.size;
    this.store.clear();
    logger.debug(`Cleared all ${count} auth sessions`);
  }

  /**
   * Get all session IDs
   */
  keys(): string[] {
    return Array.from(this.store.keys());
  }

  /**
   * Get the number of stored sessions
   */
  size(): number {
    return this.store.size;
  }

  /**
   * Check if a session exists
   */
  has(sessionId: string): boolean {
    const entry = this.store.get(sessionId);

    if (!entry) {
      return false;
    }

    // Check if expired
    if (entry.expiresAt && entry.expiresAt < new Date()) {
      this.remove(sessionId);
      return false;
    }

    return true;
  }

  /**
   * Get all valid (non-expired) sessions
   */
  getAll(): Map<string, T> {
    const result = new Map<string, T>();
    const now = new Date();

    for (const [sessionId, entry] of this.store) {
      if (!entry.expiresAt || entry.expiresAt > now) {
        result.set(sessionId, entry.authInfo);
      }
    }

    return result;
  }

  /**
   * Clean up expired sessions
   */
  cleanup(): void {
    const now = new Date();
    let removed = 0;

    for (const [sessionId, entry] of this.store) {
      if (entry.expiresAt && entry.expiresAt < now) {
        this.store.delete(sessionId);
        removed++;
      }
    }

    if (removed > 0) {
      logger.debug(`Cleaned up ${removed} expired sessions`);
    }
  }

  /**
   * Evict the oldest session (based on last accessed time)
   */
  private evictOldestSession(): void {
    let oldestSessionId: string | null = null;
    let oldestTime = new Date();

    for (const [sessionId, entry] of this.store) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestSessionId = sessionId;
      }
    }

    if (oldestSessionId) {
      this.remove(oldestSessionId);
      logger.debug(`Evicted oldest session: ${oldestSessionId}`);
    }
  }

  /**
   * Start the cleanup timer
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.options.cleanupInterval!);
  }

  /**
   * Stop the cleanup timer
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }
    this.clear();
  }
}

/**
 * Create a simple auth store with default options
 */
export function createAuthStore<T>(options?: AuthStoreOptions): IAuthStore<T> {
  return new AuthStore<T>(options);
}

/**
 * Create an auth store specifically for PropelAuthInfo
 * This is a convenience function for the common use case
 */
export function createPropelAuthStore(options?: AuthStoreOptions) {
  // Import type dynamically to avoid circular dependency
  type PropelAuthInfo = import("./propel.js").PropelAuthInfo;
  return createAuthStore<PropelAuthInfo>(options);
}
